import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Alert, 
  Dimensions,
  Platform,
  RefreshControl
} from 'react-native';
import { 
  YStack, 
  XStack, 
  Sheet,
  Progress,
  Spinner,
  H2,
  H4,
  Tabs,
  Avatar,
} from 'tamagui';
import { Card } from "@constants/Themed";
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DraggableFlatList from 'react-native-draggable-flatlist';
import * as Animatable from 'react-native-animatable';
import { 
  AntDesign, 
  MaterialIcons, 
  Feather, 
  Ionicons,
  MaterialCommunityIcons 
} from '@expo/vector-icons';

import apis from '@api/api';
import useGlobalUser from '@hooks/getGlobalUser';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DefaultScreen from '@components/Global/DefaultScreenView';
import CustomButton from '@components/UI/Button/Button';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import styles, { getModernCardStyle } from '@stylesheet';

// Import components from default implementation
import RenderWorkPackage from './Components/RenderWorkPackage';
import AddWorkPackage from './Components/AddWorkPackage';

// Import modern components we'll create
import ModernProjectHeader from './Components/Modern/ModernProjectHeader';
import ModernKanbanBoard from './Components/Modern/ModernKanbanBoard';
import ModernGanttChart from './Components/Modern/ModernGanttChart';
import ModernTeamManagement from './Components/Modern/ModernTeamManagement';
import ModernProjectAnalytics from './Components/Modern/ModernProjectAnalytics';
import ModernTaskModal from './Components/Modern/ModernTaskModal';
import ModernProjectSettings from './Components/Modern/ModernProjectSettings';
import ModernProjectFiles from './Components/Modern/ModernProjectFiles';
import CompactOverview from './Components/Modern/CompactOverview';

// Error Boundary Component for work packages
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Work Package Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={{ padding: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
            Something went wrong with the work packages
          </Text>
          <CustomButton 
            buttonText="Reload" 
            onPress={() => this.setState({ hasError: false, error: null })} 
          />
        </View>
      );
    }

    return this.props.children;
  }
}

// Types
interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string[];
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  workPackageId: string;
  tags?: string[];
  attachments?: any[];
  comments?: any[];
  timeTracking?: {
    estimated: number;
    actual: number;
  };
}

interface WorkPackage {
  _id: string;
  title: string;
  description?: string;
  tasks: Task[];
  status: 'active' | 'completed' | 'archived';
  startDate?: string;
  endDate?: string;
  progress: number;
}

interface Project {
  _id: string;
  title: string;
  description?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'archived';
  progress: number;
  startDate: string;
  endDate: string;
  team: any[];
  workPackages: WorkPackage[];
  owner: string;
  tags?: string[];
  priority: 'low' | 'medium' | 'high';
}

interface TabViewType {
  key: string;
  title: string;
  icon: string;
}

const IndividualProjectPageModern = ({ route, navigation }) => {
  const { projectId, title } = route?.params || {};
  const userId = useGlobalUser();
  const theme = themeSelector();
  const queryClient = useQueryClient();
  
  // State management
  const [activeTab, setActiveTab] = useState('kanban');
  const [viewMode, setViewMode] = useState<'kanban' | 'list' | 'board'>('kanban');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [workPackageToDelete, setWorkPackageToDelete] = useState<any>(null);
  const [deleteCallback, setDeleteCallback] = useState<(() => Promise<void>) | null>(null);

  // Work packages state from default implementation
  const [retrievedWorkPackages, setRetrievedWorkPackages] = useState([]);
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });

  // Refs for work packages
  const wpRef = useRef(null);
  const scrollRef = useRef(null);

  // Screen dimensions
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const isTablet = screenWidth > 768;

  // Tab configuration
  const tabs: TabViewType[] = [
    { key: 'work_packages', title: 'Work Packages', icon: 'layers' },
    { key: 'kanban', title: 'Kanban Board', icon: 'view-column' },
    { key: 'gantt', title: 'Gantt Chart', icon: 'timeline' },
    { key: 'team', title: 'Team', icon: 'group' },
    { key: 'analytics', title: 'Analytics', icon: 'analytics' },
    { key: 'files', title: 'Files', icon: 'folder' },
    { key: 'settings', title: 'Settings', icon: 'settings' }
  ];

  // Work package callback function
  const _addWP = useCallback((wp) => {
    console.log('Adding work package:', wp?._id);
    setRetrievedWorkPackages((prev) => [...prev, wp]);
  }, []);

  // Fetch project data with optimized cache management
  const { 
    data: projectData, 
    isLoading, 
    error, 
    refetch,
    isFetching 
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId || !userId) {
        throw new Error('Missing projectId or userId');
      }

      // Add debug API calls to test new endpoints
      try {
        console.log('🔍 Testing debug endpoints...', { projectId, userId });

        // Test the tasks endpoint
        const tasksResponse = await apis.getProjectTasks(projectId, userId);
        console.log('📋 Tasks endpoint response:', tasksResponse.data);
      } catch (debugError) {
        console.warn('⚠️ Debug endpoints failed (expected if not implemented):', debugError);
      }

      const response = await apis.getProject(projectId, userId);
      const projectData = response.data.data || response.data || {};
      const workPackages = projectData?.work_packages || [];
      
      // Enhanced debug logging
      console.log('📊 Enhanced Project API Analysis:', {
        fullResponse: response,
        projectData,
        workPackagesCount: workPackages.length,
        workPackagesStructure: workPackages.map(wp => ({
          id: wp._id,
          title: wp.work_package_title,
          taskListLength: wp.task_list?.length || 0,
          taskListSample: wp.task_list?.slice(0, 2).map(task => ({
            id: task._id,
            task_title: task.task_title,
            title: task.title,
            status: task.status
          })) || []
        })),
        totalTasksAcrossWorkPackages: workPackages.reduce((total, wp) => total + (wp.task_list?.length || 0), 0)
      });

      // Check if project data has the expected structure
      if (!projectData._id) {
        console.warn('⚠️ Project data missing _id field:', projectData);
      }

      if (!workPackages.length) {
        console.warn('⚠️ No work packages found in project:', projectId);
      } else {
        const packagesWithTasks = workPackages.filter(wp => wp.task_list?.length > 0);
        console.log(`📈 Found ${packagesWithTasks.length}/${workPackages.length} work packages with tasks`);
      }

      // Update work packages state
      setRetrievedWorkPackages(workPackages);
      setHighlightedItem(workPackages[0] || null);
      
      return response.data;
    },
    enabled: !!projectId && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Add refetchOnWindowFocus: false to prevent unnecessary refetches
    refetchOnWindowFocus: false,
    // Prevent automatic background refetches that can cause memory issues
    refetchInterval: false,
  });

  // Mutations with optimized invalidation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: Partial<Task> }) => {
      // Create proper id structure for modifyTask API
      const taskIdStructure = {
        _id: taskId,
        dataId: projectId,
        extraId: updates.workPackageId || 'unknown' // fallback if workPackageId not provided
      };
      return await apis.modifyTask(taskIdStructure, updates);
    },
    onSuccess: () => {
      // Use specific query invalidation instead of broad invalidation
      queryClient.invalidateQueries({ 
        queryKey: ['project', projectId],
        exact: true 
      });
    },
    onError: (error) => {
      console.error('Update task error:', error);
      Alert.alert('Error', 'Failed to update task');
    }
  });

  const moveTaskMutation = useMutation({
    mutationFn: async ({ 
      taskId, 
      sourceStatus, 
      destinationStatus, 
      sourceIndex,
      destinationIndex,
      workPackageId
    }: {
      taskId: string;
      sourceStatus: string;
      destinationStatus: string;
        sourceIndex: number;
        destinationIndex: number;
      workPackageId: string;
    }) => {
      console.log('🔄 Moving task with enhanced API:', {
        taskId,
        sourceStatus,
        destinationStatus,
        sourceIndex,
        destinationIndex,
        workPackageId
      });

      // Use the new enhanced moveTask API
      return await apis.moveTask(projectId, taskId, {
        sourceStatus,
        destinationStatus,
        sourceIndex,
        destinationIndex,
        workPackageId
      });
    },
    onSuccess: (data) => {
      console.log('✅ Task moved successfully:', data);
      // No need to invalidate queries since we're using optimistic updates
      // The UI is already updated locally
    },
    onError: (error) => {
      console.error('❌ Move task error:', error);
      Alert.alert('Error', 'Failed to move task');
    }
  });

  // Handlers with proper dependencies and memoization
  const handleTabChange = useCallback((tabKey: string) => {
    setActiveTab(tabKey);
  }, []);

  const handleTaskEdit = useCallback((task: Task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  }, []);

  const handleTaskCreate = useCallback(() => {
    setEditingTask(null);
    setShowTaskModal(true);
  }, []);

  const handleWorkPackageDelete = useCallback((workPackage: any, deleteFunction: () => Promise<void>) => {
    setWorkPackageToDelete(workPackage);
    setDeleteCallback(() => deleteFunction);
    setDeleteModalVisible(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteCallback) {
      try {
        await deleteCallback();
        setDeleteModalVisible(false);
        setWorkPackageToDelete(null);
        setDeleteCallback(null);
      } catch (error) {
        console.error('Delete error:', error);
        Alert.alert('Error', 'Failed to delete work package');
      }
    }
  }, [deleteCallback]);

  const handleTaskDragEnd = useCallback((result: any) => {
    if (!result.destination) return;

    const { source, destination, draggableId: taskId } = result;
    
    console.log('🎯 Drag end result:', {
      source,
      destination,
      taskId,
      sourceIndex: source.index,
      destinationIndex: destination.index
    });

    // Find the task's workPackageId from the project data
    const workPackages = projectData?.work_packages || [];
    let foundWorkPackageId = '';
    let foundTask = null;

    for (const wp of workPackages) {
      const task = wp.task_list?.find(t => t._id === taskId);
      if (task) {
        foundWorkPackageId = wp._id;
        foundTask = task;
        break;
      }
    }

    if (foundWorkPackageId && foundTask) {
      // Store original state for rollback
      const originalWorkPackages = JSON.parse(JSON.stringify(retrievedWorkPackages));

      // Optimistically update the local state immediately
      const updatedWorkPackages = retrievedWorkPackages.map(wp => {
        if (wp._id === foundWorkPackageId) {
          const updatedTaskList = [...(wp.task_list || [])];

          // Remove task from source position
          const sourceIndex = updatedTaskList.findIndex(t => t._id === taskId);
          if (sourceIndex !== -1) {
            updatedTaskList.splice(sourceIndex, 1);
          }

          // Insert task at destination position
          const taskToMove = { ...foundTask, status: destination.droppableId };
          updatedTaskList.splice(destination.index, 0, taskToMove);

          return {
            ...wp,
            task_list: updatedTaskList
          };
        }
        return wp;
      });

      // Update local state immediately for instant UI feedback
      setRetrievedWorkPackages(updatedWorkPackages);

      console.log('🎯 Optimistic update applied, calling backend API:', {
        taskId,
        sourceStatus: source.droppableId,
        destinationStatus: destination.droppableId,
        sourceIndex: source.index,
        destinationIndex: destination.index,
        workPackageId: foundWorkPackageId
      });

      // Make the API call
      moveTaskMutation.mutate({
        taskId,
        sourceStatus: source.droppableId,
        destinationStatus: destination.droppableId,
        sourceIndex: source.index,
        destinationIndex: destination.index,
        workPackageId: foundWorkPackageId
      }, {
        onError: (error) => {
          console.error('❌ Move task failed, rolling back optimistic update:', error);
          // Rollback to original state on error
          setRetrievedWorkPackages(originalWorkPackages);
        }
      });
    } else {
      console.error('Could not find workPackageId for task:', taskId);
      Alert.alert('Error', 'Could not move task - work package not found');
    }
  }, [moveTaskMutation, projectData, retrievedWorkPackages, setRetrievedWorkPackages]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const handleBulkAction = useCallback((action: string) => {
    if (selectedTasks.length === 0) {
      Alert.alert('No tasks selected', 'Please select tasks to perform this action.');
      return;
    }

    switch (action) {
      case 'delete':
        Alert.alert(
          'Delete Tasks',
          `Are you sure you want to delete ${selectedTasks.length} tasks?`,
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'Delete', 
              style: 'destructive',
              onPress: async () => {
                try {
                  // Find workPackageId for each task before deleting
                  const workPackages = projectData?.work_packages || [];
                  const deletePromises = selectedTasks.map(taskId => {
                    // Find the workPackageId for this task
                    let foundWorkPackageId = '';
                    for (const wp of workPackages) {
                      const task = wp.task_list?.find(t => t._id === taskId);
                      if (task) {
                        foundWorkPackageId = wp._id;
                        break;
                      }
                    }
                    
                    if (foundWorkPackageId) {
                      const taskIdStructure = {
                        _id: taskId,
                        dataId: projectId,
                        extraId: foundWorkPackageId
                      };
                      return apis.deleteTask(taskIdStructure).catch(err => console.warn(`Failed to delete task ${taskId}:`, err));
                    } else {
                      console.warn(`Could not find workPackageId for task ${taskId}`);
                      return Promise.reject(new Error(`Work package not found for task ${taskId}`));
                    }
                  });
                  
                  await Promise.allSettled(deletePromises);
                  setSelectedTasks([]);
                  await refetch();
                } catch (error) {
                  console.error('Bulk delete error:', error);
                  Alert.alert('Error', 'Some tasks could not be deleted');
                }
              }
            }
          ]
        );
        break;
      case 'archive':
        // Implement bulk archive
        break;
      case 'assign':
        // Show team member selection
        setShowTeamModal(true);
        break;
    }
  }, [selectedTasks, refetch]); // Remove apis from dependencies to prevent recreation

  // Work package rendering functions
  const keyExtractor = useCallback((item: any, index: number) => {
    const key = item?._id || `item-${index}`;
    return key;
  }, []);

  const renderWorkPackageItem = useCallback(({ item, isActive, drag }) => {
    if (!item || !item._id) {
      return <View style={{ height: 50, backgroundColor: 'red', margin: 5 }} />;
    }

    // Apply modern card styling
    const modernCardStyles = getModernCardStyle(theme === 'light', 'medium');
    
    return (
      <Card style={[
        {
          width: 380,
          minHeight: "100%",
          margin: 10,
          overflow: 'hidden',
        },
      ]}>
        <RenderWorkPackage 
          setRetrievedWorkPackages={setRetrievedWorkPackages} 
          setHighlightedItem={setHighlightedItem} 
          item={item} 
          refresh={refetch} 
          drag={drag} 
          isActive={isActive} 
          projectGantt={false}
          projectId={projectId}
          highlightedItem={highlightedItem}
          taskRef={null}
          onDeleteWorkPackage={handleWorkPackageDelete}
        />
      </Card>
    );
  }, [refetch, projectId, highlightedItem, theme, handleWorkPackageDelete]);

  // Helper function for title case
  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  // Optimized computed values with proper memoization
  const project = useMemo(() => {
    const projectDataTyped = projectData as any;
    return projectDataTyped?.data as Project;
  }, [projectData]);

  // Fix workPackages mapping - extract from project data with proper task mapping
  const workPackages = useMemo(() => {
    if (!project && !retrievedWorkPackages?.length) return [];

    // Use retrievedWorkPackages if available (from API response), otherwise fall back to project.workPackages
    const wpSource = retrievedWorkPackages?.length ? retrievedWorkPackages : ((project as any)?.work_packages || project?.workPackages || []);

    return wpSource.map(wp => ({
      ...wp,
      tasks: (wp.task_list || wp.tasks || []).map(task => ({
        ...task,
        // Map backend field names to frontend expectations
        title: task.title || task.task_title || '',
        status: task.status || 'todo', // Default status if missing
        priority: task.priority || 'medium',
        assignedTo: task.assigned_to || task.assignedTo || [],
        workPackageId: wp._id || wp.id,
        // Ensure required fields exist
        _id: task._id || task.id,
        description: task.description || '',
        createdAt: task.createdAt || task.created_at || new Date().toISOString(),
        updatedAt: task.updatedAt || task.updated_at || new Date().toISOString(),
      }))
    }));
  }, [project, retrievedWorkPackages]);

  const allTasks = useMemo(() => workPackages.flatMap(wp => wp.tasks || []), [workPackages]);
  
  const filteredTasks = useMemo(() => {
    if (!allTasks.length) return [];
    
    return allTasks.filter(task => {
      const matchesSearch = !searchQuery || 
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filterStatus === 'all' || 
        (filterStatus === 'active' && ['todo', 'in-progress', 'review'].includes(task.status)) ||
        (filterStatus === 'completed' && task.status === 'done');
      
      return matchesSearch && matchesFilter;
    });
  }, [allTasks, searchQuery, filterStatus]);

  const tasksByStatus = useMemo(() => {
    const groups = {
      'todo': [],
      'in-progress': [],
      'review': [],
      'done': []
    };
    
    console.log('Creating tasksByStatus:', {
      filteredTasksCount: filteredTasks.length,
      filteredTasks,
      allTasksCount: allTasks.length,
      workPackagesCount: workPackages.length,
      workPackages: workPackages.map(wp => ({
        id: wp._id,
        title: wp.work_package_title,
        tasksCount: wp.tasks?.length || 0
      }))
    });

    filteredTasks.forEach(task => {
      if (groups[task.status]) {
        groups[task.status].push(task);
      } else {
        // Handle tasks with invalid status by putting them in 'todo'
        console.warn('Task with invalid status:', task.status, 'Task:', task);
        groups['todo'].push(task);
      }
    });
    
    console.log('Final tasksByStatus groups:', {
      todo: groups.todo.length,
      'in-progress': groups['in-progress'].length,
      review: groups.review.length,
      done: groups.done.length
    });

    return groups;
  }, [filteredTasks, allTasks, workPackages]);

  // Pass header functions to navigation with proper cleanup
  useEffect(() => {
    console.log("Setting navigation params:", {
      project: project?.title,
      allTasksLength: allTasks?.length,
      selectedTasksLength: selectedTasks?.length,
      navigationExists: !!navigation
    });

    if (project && navigation) {
      try {
        navigation.setParams({
          project,
          headerProps: {
            tasks: allTasks || [],
            selectedTasks: selectedTasks,
            // Remove non-serializable functions from navigation state
            // These will be handled through context or other means
          }
        });
        console.log("Navigation params set successfully");
      } catch (error) {
        console.error("Error setting navigation params:", error);
      }
    }

    // Cleanup function to prevent memory leaks
    return () => {
      try {
        if (navigation) {
          navigation.setParams({
            project: null,
            headerProps: null
          });
        }
      } catch (error) {
        console.warn("Cleanup navigation params error:", error);
      }
    };
  }, [project, allTasks, selectedTasks, navigation, handleBulkAction]);

  // Component cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear all state on unmount
      setSelectedTasks([]);
      setSearchQuery('');
      setFilterStatus('all');
      setEditingTask(null);
      setShowTaskModal(false);
      setShowTeamModal(false);
      setShowSettingsModal(false);
      
      // Clear work packages state
      setRetrievedWorkPackages([]);
      setHighlightedItem(null);
      
      // Clear query cache for this specific project to prevent memory accumulation
      queryClient.removeQueries({ 
        queryKey: ['project', projectId],
        exact: true 
      });
      
      console.log('IndividualProjectPageModern: Component cleanup completed');
    };
  }, [projectId, queryClient]);

  // Test debug endpoints function
  const testDebugEndpoints = useCallback(async () => {
    if (!projectId || !userId) {
      Alert.alert('Error', 'Missing project ID or user ID');
      return;
    }

    try {
      console.log('🧪 Testing debug endpoints manually...');

      // Test tasks endpoint  
      const tasksResponse = await apis.getProjectTasks(projectId, userId);
      console.log('📋 Manual tasks test result:', tasksResponse.data);

      Alert.alert(
        'Debug Test Complete',
        `Tasks endpoint returned ${tasksResponse.data?.data?.taskCount || 0} total tasks. Check console for details.`
      );
    } catch (error) {
      console.error('🚨 Debug endpoints test failed:', error);
      Alert.alert('Debug Test Failed', error.message || 'Unknown error');
    }
  }, [projectId, userId]);

  // Effect to handle projectId changes
  useEffect(() => {
    setRetrievedWorkPackages([]);
    setHighlightedItem(null);
  }, [projectId]);

  // Loading state
  if (isLoading) {
    return (
      <DefaultScreen>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <Spinner size="large" />
          <Text style={{ marginTop: 16, color: theme ? '#000' : '#fff' }}>
            Loading project...
          </Text>
        </View>
      </DefaultScreen>
    );
  }

  // Error state
  if (error) {
    return (
      <DefaultScreen>
        <View style={[styles.centerEverything, { flex: 1, padding: 20 }]}>
          <MaterialIcons 
            name="error-outline" 
            size={48} 
            color={theme ? '#dc2626' : '#ef4444'} 
          />
          <Text style={{ 
            marginTop: 16, 
            fontSize: 18, 
            fontWeight: '600',
            color: theme ? '#000' : '#fff',
            textAlign: 'center'
          }}>
            Failed to load project
          </Text>
          <Text style={{ 
            marginTop: 8, 
            color: theme ? '#6b7280' : '#9ca3af',
            textAlign: 'center'
          }}>
            {error.message}
          </Text>
          <View style={{ marginTop: 20 }}>
            <CustomButton
              buttonText="Retry"
              onPress={() => refetch()}
            />
          </View>
        </View>
      </DefaultScreen>
    );
  }

  // Main render function
  const renderTabContent = () => {
    switch (activeTab) {
      case 'work_packages':
        return (
          <View style={{ flex: 1 }}>
            {/* Work Packages Header */}
            <View style={{
              padding: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#374151',
            }}>
              <XStack justifyContent="space-between" alignItems="center">
                <H4 color={theme ? '#111827' : '#ffffff'}>
                  Work Packages ({retrievedWorkPackages?.length || 0})
                </H4>
                <XStack gap={8}>
                  {/* Debug button for testing API endpoints */}
                  <TouchableOpacity
                    onPress={testDebugEndpoints}
                    style={{
                      backgroundColor: '#f59e0b',
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      borderRadius: 8,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <MaterialIcons name="bug-report" size={18} color="#ffffff" />
                    <Text style={{ color: '#ffffff', fontWeight: '600', fontSize: 12 }}>
                      Debug
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => wpRef.current?.openModal?.()}
                    style={{
                      backgroundColor: '#3b82f6',
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 8,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <MaterialIcons name="add" size={20} color="#ffffff" />
                    <Text style={{ color: '#ffffff', fontWeight: '600' }}>
                      Add Work Package
                    </Text>
                  </TouchableOpacity>
                </XStack>
              </XStack>
            </View>

            {/* Work Packages List */}
            <View style={{ flex: 1 }}>
              {Array.isArray(retrievedWorkPackages) && retrievedWorkPackages.length > 0 ? (
                <ErrorBoundary>
                  <DraggableFlatList
                    data={retrievedWorkPackages}
                    renderItem={renderWorkPackageItem}
                    style={{ paddingVertical: 5 }}
                    ref={scrollRef}
                    onDragEnd={({ data }) => {
                      if (data && Array.isArray(data)) {
                        setRetrievedWorkPackages(data);
                      }
                    }}
                    keyExtractor={keyExtractor}
                    contentContainerStyle={{
                      paddingLeft: 20,
                      paddingVertical: 10,
                      paddingBottom: 30,
                      paddingRight: 20,
                      alignSelf: "stretch",
                      minHeight: Dimensions.get('window').height * 0.68
                    }}
                    horizontal={true}
                    showsHorizontalScrollIndicator={false}
                    removeClippedSubviews={true}
                    maxToRenderPerBatch={10}
                    windowSize={5}
                    initialNumToRender={5}
                    onScrollToIndexFailed={(info) => {
                      console.warn('Scroll to index failed:', info);
                      setTimeout(() => {
                        try {
                          scrollRef.current?.scrollToIndex({
                            index: info.index,
                            animated: true
                          });
                        } catch (error) {
                          console.warn('Retry scroll failed:', error);
                        }
                      }, 100);
                    }}
                  />
                </ErrorBoundary>
              ) : (
                <View style={{ 
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 40 
                }}>
                  <MaterialIcons 
                    name="layers" 
                    size={64} 
                      color={theme ? '#d1d5db' : '#4b5563'} 
                  />
                  <Text style={{
                    fontSize: 18,
                    fontWeight: '600',
                      color: theme ? '#6b7280' : '#9ca3af',
                    marginTop: 16,
                    textAlign: 'center'
                  }}>
                    No Work Packages Yet
                  </Text>
                  <Text style={{
                    fontSize: 14,
                      color: theme ? '#9ca3af' : '#6b7280',
                    marginTop: 8,
                    textAlign: 'center',
                    maxWidth: 300
                  }}>
                    Create your first work package to organize tasks and track progress
                  </Text>
                  <TouchableOpacity
                    onPress={() => wpRef.current?.openModal?.()}
                    style={{
                      backgroundColor: '#3b82f6',
                      paddingHorizontal: 24,
                      paddingVertical: 12,
                      borderRadius: 12,
                      marginTop: 20,
                    }}
                  >
                    <Text style={{ color: '#ffffff', fontWeight: '600' }}>
                      Create Work Package
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        );
      case 'kanban':
        return (
          <ModernKanbanBoard
            tasks={tasksByStatus}
            onTaskEdit={handleTaskEdit}
            onTaskCreate={handleTaskCreate}
            onTaskDragEnd={handleTaskDragEnd}
            selectedTasks={selectedTasks}
            onTaskSelect={setSelectedTasks}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        );
      
      case 'gantt':
        return (
          <ModernGanttChart
            project={project}
            workPackages={workPackages}
            tasks={allTasks}
            onTaskEdit={handleTaskEdit}
          />
        );
      
      case 'team':
        return (
          <ModernTeamManagement
            project={project}
            onAddTeamMember={() => setShowTeamModal(true)}
            onTeamUpdated={refetch}
          />
        );
      
      case 'analytics':
        return (
          <ModernProjectAnalytics
            project={project}
            tasks={allTasks}
            workPackages={workPackages}
            detailed={true}
          />
        );
      
      case 'files':
        return (
          <ModernProjectFiles
            projectId={project._id}
            files={[
              {
                id: '1',
                name: 'Project Requirements.pdf',
                type: 'file',
                size: 2048576,
                uploadedAt: new Date('2024-01-15'),
                uploadedBy: 'John Doe',
                url: '/files/requirements.pdf'
              },
              {
                id: '2',
                name: 'Design Assets',
                type: 'folder',
                uploadedAt: new Date('2024-01-10'),
                uploadedBy: 'Jane Smith'
              },
              {
                id: '3',
                name: 'Meeting Notes.docx',
                type: 'file',
                size: 524288,
                uploadedAt: new Date('2024-01-12'),
                uploadedBy: 'Mike Johnson',
                url: '/files/meeting-notes.docx'
              }
            ]}
            onUpload={(files) => {
              console.log('Upload files:', files);
              // TODO: Implement file upload
            }}
            onDelete={(fileId) => {
              console.log('Delete file:', fileId);
              // TODO: Implement file deletion
            }}
            onDownload={(file) => {
              console.log('Download file:', file);
              // TODO: Implement file download
            }}
          />
        );
      
      case 'settings':
        return (
          <ModernProjectSettings
            project={project}
            onSave={(updates) => {
              // Handle project updates
              console.log('Project updates:', updates);
            }}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <DefaultScreen
      fetchingState={isFetching}
      removeMargin
      style={{ paddingTop: 0, maxHeight: "70dvh" }}
      layoutCallback={setPageDimensions}
    >
      {/* Add Work Package Modal */}
      <AddWorkPackage
        ref={wpRef}
        callback={_addWP}
        projectId={projectId}
        projectTitle={project?.title || title || ''}
      />

      {/* Modern Tab Navigation */}
      <View style={{
        backgroundColor: theme ? '#ffffff' : '#1f2937',
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e5e7eb' : '#374151',
      }}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 20 }}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              onPress={() => handleTabChange(tab.key)}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 12,
                marginRight: 8,
                borderBottomWidth: 2,
                borderBottomColor: activeTab === tab.key 
                  ? '#3b82f6' 
                  : 'transparent',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <MaterialIcons
                name={tab.icon as any}
                size={20}
                color={activeTab === tab.key 
                  ? '#3b82f6' 
                  : theme ? '#6b7280' : '#9ca3af'
                }
              />
              <Text style={{
                marginLeft: 8,
                fontSize: 14,
                fontWeight: activeTab === tab.key ? '600' : '400',
                color: activeTab === tab.key 
                  ? '#3b82f6' 
                  : theme ? '#6b7280' : '#9ca3af'
              }}>
                {tab.title}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      <View style={{ flex: 1, alignSelf: "stretch", width:"100%", maxWidth: "100%" }}>
        {renderTabContent()}
      </View>

      {/* Modern Task Modal */}
      <ModernTaskModal
        visible={showTaskModal}
        task={editingTask}
        project={project}
        workPackages={retrievedWorkPackages}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null); // Clear editing task to prevent memory retention
        }}
        onSave={async (taskData) => {
          try {
            if (editingTask) {
              await updateTaskMutation.mutateAsync({
                taskId: editingTask._id,
                updates: taskData
              });
            } else {
              // Create proper id structure for addTask API
              const idStructure = {
                globalId: userId,
                dataId: projectId,
                extraId: taskData.workPackageId
              };
              await apis.addTask(taskData, idStructure);
            }
            setShowTaskModal(false);
            setEditingTask(null); // Clear editing task
            await refetch();
          } catch (error) {
            console.error('Task save error:', error);
            Alert.alert('Error', 'Failed to save task');
          }
        }}
      />

      {/* Floating Action Button */}
      <TouchableOpacity
        onPress={handleTaskCreate}
        style={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: 28,
          backgroundColor: '#3b82f6',
          justifyContent: 'center',
          alignItems: 'center',
          elevation: 8,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
        }}
      >
        <MaterialIcons name="add" size={24} color="#ffffff" />
      </TouchableOpacity>

      <DeleteConfirmModal
        visibleState={deleteModalVisible}
        setState={setDeleteModalVisible}
        title={workPackageToDelete?.title}
        deleteFunction={handleConfirmDelete}
      />
    </DefaultScreen>
  );
};

export default React.memo(IndividualProjectPageModern);
